import { Alert, Button } from "antd";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import { LogoWhite } from "../../../assets/svg/Svgs";
import {
  APP_NAME,
  LOGIN,
  REGISTER,
  TAGLINE,
} from "../../../constants/constants";
import useForm from "../../../hooks/common/useForm";
import useSet from "../../../hooks/common/useSet";
import { formHooks } from "../../../hooks/handlers/formHooks";
import { RenderForm } from "../../../utils/formUtils";
import { currentYear } from "../../../utils/helpers";
import { LoginForm } from "./LoginForm";

export const AuthPage = () => {
  const { id } = useParams();
  return <PageHandler key={id} />;
};

const PageHandler = () => {
  const { id: source = "" } = useParams();
  const isLogin = source === LOGIN;

  return (
    <Wrapper className="vh100 bgGradient white flexColumn" isLogin={isLogin}>
      {/* Glow overlay */}
      <img id="overlay" alt={APP_NAME} src={LogoWhite} />

      <TopWrapper className="flexGrow container">
        <GridWrapper>
          {/* LEFT — brand panel */}
          <LeftWrapper>
            <div id="logo" className="flexNullCenter gap15">
              <img
                alt={APP_NAME}
                src={LogoWhite}
                width={40}
                height={40}
                style={{ filter: "brightness(2000%)" }}
              />
              <div className="extraBold" style={{ fontSize: 25 }}>
                everpesa
              </div>
            </div>

            <div data-aos="fade-up" data-aos-duration="600" data-aos-delay="100">
              <h1 className="extraBold">
                {isLogin ? (
                  <>
                    Welcome <br /> <span className="light">back to</span> {APP_NAME}.
                  </>
                ) : (
                  <>
                    Sign up <span className="light">with</span>
                    <br /> {APP_NAME}.
                  </>
                )}
              </h1>
            </div>

            <div
              className="light font16"
              data-aos="fade-up"
              data-aos-duration="600"
              data-aos-delay="200"
            >
              {TAGLINE}
            </div>

            <div
              className="light font12 mb20"
              style={{ marginTop: 25 }}
              data-aos="fade-up"
              data-aos-duration="600"
              data-aos-delay="300"
            >
              Simplifying the way you manage your investments
            </div>
          </LeftWrapper>

          {/* RIGHT — login OR register form */}
          <RightWrapper className="flexEnd">
            {isLogin ? <LoginForm /> : <RegisterFormShell source={source} />}
          </RightWrapper>
        </GridWrapper>
      </TopWrapper>

      <div className="font12 textCenter" style={{ padding: 30 }}>
        © {currentYear()} {APP_NAME}. All rights reserved.
      </div>
    </Wrapper>
  );
};

/* Register form preserved from the original AuthPage — only login was redesigned. */
const RegisterFormShell = ({ source }: { source: string }) => {
  const { error, setError, uploadData, submitting } = useSet();
  const { form, updateFormValues, onChange, validateForm } = useForm();

  const hook = formHooks[source];
  const { fields, onSubmit: formSubmit, route } = hook({
    form,
    updateFormValues,
    error,
  });

  const onSubmit = (e: any) => {
    e.preventDefault();
    if (validateForm(fields, setError)) {
      const payload = { ...form };
      const submitData = formSubmit({ payload });
      if (submitData) return uploadData(submitData);
    }
  };

  const errorMessage = (() => {
    if (!error) return null;
    if (typeof error === "string") return error;
    if (error?.message) return error.message;
    if (typeof error === "object") {
      const firstKey = Object.keys(error).find((k) => k !== "message");
      if (firstKey) {
        const val = error[firstKey];
        return Array.isArray(val) ? val[0] : val;
      }
    }
    return "An error occurred";
  })();

  return (
    <form onSubmit={onSubmit}>
      <FormWrapper
        className="r15 whiteBg black flexColumn gap20 x100"
        data-aos="fade-up"
        data-aos-duration="400"
        isRegister
      >
        <div className="extraBold textDark" style={{ fontSize: 25 }}>
          Create Account
        </div>
        <div className="light font13">
          Choose your role and fill in to get started.
        </div>

        <FormGridWrapper columns="1fr 1fr">
          {RenderForm({ fields, form, updateFormValues, onChange, error })}
        </FormGridWrapper>

        {errorMessage && (
          <Alert className="font12" message={errorMessage} type="error" showIcon />
        )}

        <Button
          type="primary"
          htmlType="submit"
          className="capitalize noShadow r7"
          loading={submitting}
          block
          size="large"
        >
          Create Account
        </Button>

        <div className="flexCenter gap5">
          <div className="textCenter font12 light">Already have an account?</div>
          <Link
            to={`/auth/${route}`}
            className="font13 main semiBold capitalize"
          >
            Login
          </Link>
        </div>
      </FormWrapper>
    </form>
  );
};

// ─── Styles ──────────────────────────────────────────────────────

const Wrapper = styled.div<{ isLogin?: boolean }>`
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;

  #overlay {
    opacity: 0.1;
    position: absolute;
    left: -10%;
    bottom: -10%;
    width: 700px;
    pointer-events: none;
  }

  @media (max-width: 960px) {
    min-height: 100svh;

    #overlay {
      left: 10%;
    }
  }
`;

const TopWrapper = styled.div`
  z-index: 1;
  padding: 80px 0 60px;
  flex: 1;
  @media (max-width: 960px) {
    padding: 40px 0 50px;
  }
`;

const LeftWrapper = styled.div`
  h1 {
    font-size: 50px;
  }

  #logo {
    margin-bottom: 100px;
  }
  @media (max-width: 960px) {
    h1 {
      font-size: xx-large;
    }
    #logo {
      margin-bottom: 20px;
    }
  }
`;

const RightWrapper = styled.div`
  display: flex;
  justify-content: flex-end;

  @media (max-width: 960px) {
    justify-content: center;
    padding: 0;
    padding-top: 40px;
    padding-bottom: 20px;
  }
`;

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  padding: 20px;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const FormGridWrapper = styled.div<{ columns?: string }>`
  display: grid;
  grid-template-columns: ${({ columns }) => columns || "1fr 1fr"};
  gap: 20px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const FormWrapper = styled.div<{ isRegister?: boolean }>`
  max-width: ${({ isRegister }) => (isRegister ? "560px" : "500px")};
  padding: 30px;
  width: 100%;

  @media (max-width: 960px) {
    width: 100%;
    padding: 20px;
    max-width: 100%;
  }
`;